"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { MERCHANT } from "@/lib/legal";
import { ModalOverlay } from "@/components/ModalOverlay";

// Uygulamanın her sayfasının altındaki footer (AppShell üzerinden) ve /legal
// sayfasının altı.
//
// Yerleşim TEK SATIR: solda marka + telif, sağda üç bağlantı. Daha önce üç sütunlu
// bir bloktu ve sayfanın alt üçte birini yiyordu — sütunların yüksekliğini yedi
// hukuki bağlantı ile satıcı kimliğinin tam adresi belirliyordu. İkisi de footer'da
// sürekli açık durmak zorunda değil:
//
//   • Yedi hukuki bağlantının hepsi zaten aynı sayfaya (yalnızca çapası farklı)
//     gidiyordu; kullanıcı için yedi ayrı yer değil, tek bir yerin içindekiler
//     tablosuydu. Artık tek bağlantı, belge başlıkları /legal sayfasının içinde.
//   • Satıcı kimliği bir tıkla açılan pop-up'ta. Elektronik Ticarette Hizmet
//     Sağlayıcı ve Aracı Hizmet Sağlayıcılar Hakkında Yönetmelik m.5 bu bilginin
//     "kolayca ulaşılabilir" olmasını ister, footer'da sürekli AÇIK durmasını değil:
//     her sayfada duran adı konmuş bir "İletişim" düğmesi bu ölçütü karşılar. Bilgi
//     ayrıca /legal içinde de yazılı durur, yani tek erişim yolu bu pop-up değildir.
//     Tek doğru kaynak lib/legal/shared.ts MERCHANT sabitidir.
//
// Başlıklar mevcut çeviri anahtarlarından geliyor (legal.title, footer.contact);
// yalnızca pop-up'ın kapatma etiketi için footer.close eklendi.
//
// Renk: kenar çubuğuyla aynı `.chrome-tinted` kapsamı (globals.css): seçilen
// accent'in koyu tonu zemin, yazılar açık. Pop-up body'ye portal'landığı için
// bu kapsamın DIŞINDA kalır ve normal sayfa renklerini okur.

const linkClass =
  "text-xs text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors";

function ContactModal({ onClose }: { onClose: () => void }) {
  const t = useTranslations("footer");

  useEffect(() => {
    function onEsc(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  return (
    <ModalOverlay
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-title"
        className="w-full max-w-md rounded-2xl bg-[var(--surface)] border border-[var(--border)] shadow-xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2
            id="contact-modal-title"
            className="text-lg font-semibold text-[var(--text-primary)]"
            style={{ fontFamily: "var(--font-lora)" }}
          >
            {t("contact")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("close")}
            className="shrink-0 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm font-medium text-[var(--text-primary)]">
          {MERCHANT.legalName}
        </p>
        <p className="text-xs text-[var(--text-muted)]">{t("merchantType")}</p>

        {/* Etiketsiz düz blok: adres/e-posta/telefon için ayrı çeviri anahtarı
            açmak, on bir dilde çevrilecek üç yeni satır demekti ve kazancı yalnızca
            zaten kendini açıklayan üç alanın başlığı olurdu. Vergi dairesi satırı
            istisna — sayı tek başına ne olduğunu söylemiyor. */}
        <p className="mt-4 text-xs leading-relaxed text-[var(--text-secondary)]">
          {MERCHANT.address}
          <br />
          {t("taxLabel")}: {MERCHANT.taxOffice} / {MERCHANT.taxNo}
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
          <a href={`mailto:${MERCHANT.email}`} className="text-[var(--accent)] hover:underline">
            {MERCHANT.email}
          </a>
          {MERCHANT.phone && (
            <a
              href={`tel:${MERCHANT.phone.replace(/\s/g, "")}`}
              className="text-[var(--accent)] hover:underline"
            >
              {MERCHANT.phone}
            </a>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}

export function Footer({
  legalBase = "/legal",
  className = "",
}: { legalBase?: string; className?: string } = {}) {
  const t = useTranslations("footer");
  const tLegal = useTranslations("legal");
  const year = new Date().getFullYear();
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <footer className={`chrome-tinted mt-auto border-t border-[var(--border)] ${className}`}>
      <div className="max-w-5xl mx-auto px-5 py-5 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <p className="text-xs text-[var(--text-muted)]">
          <span
            className="text-[13px] font-semibold text-[var(--text-primary)]"
            style={{ fontFamily: "var(--font-lora)" }}
          >
            PsyPractice
          </span>
          <span className="mx-2 opacity-50">·</span>© {year}
          <span className="mx-2 opacity-50">·</span>{t("rights")}
        </p>

        <nav className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <Link href={legalBase} className={linkClass}>
            {tLegal("title")}
          </Link>
          <button type="button" onClick={() => setContactOpen(true)} className={linkClass}>
            {t("contact")}
          </button>
          <Link href={`${legalBase}#about`} className={linkClass}>
            {t("about")}
          </Link>
        </nav>
      </div>

      {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
    </footer>
  );
}
